import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Home, 
  Salad, 
  BookOpen, 
  Leaf, 
  Puzzle, 
  Lightbulb, 
  Settings, 
  LogOut,
  Menu,
  X,
  Users,
  Mail,
  Activity,
  UserCog,
  History,
  RefreshCw,
  Sparkles,
  ClipboardCheck,
  Key
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const navItems = [
  { to: '/admin/dashboard', icon: Home, label: 'Dashboard' },
  { to: '/admin/foods', icon: Salad, label: 'Foods' },
  { to: '/admin/recipes', icon: BookOpen, label: 'Recipes' },
  { to: '/admin/rescue-actions', icon: Leaf, label: 'Rescue Actions' },
  { to: '/admin/combos', icon: Puzzle, label: 'Smart Combos' },
  { to: '/admin/tips', icon: Lightbulb, label: 'Daily Tips' },
  { to: '/admin/config', icon: Settings, label: 'App Config' },
  { divider: true, label: 'AI & Import' },
  { to: '/admin/ai-generate', icon: Sparkles, label: 'AI Generate' },
  { to: '/admin/review-queue', icon: ClipboardCheck, label: 'Review Queue' },
  { divider: true, label: 'User Management' },
  { to: '/admin/users', icon: Users, label: 'Users' },
  { to: '/admin/waitlist', icon: Mail, label: 'Waitlist' },
  { to: '/admin/health', icon: Activity, label: 'System Health' },
  { divider: true, label: 'Settings' },
  { to: '/admin/sync', icon: RefreshCw, label: 'Sync Management' },
  { to: '/admin/api-credentials', icon: Key, label: 'API Credentials' },
  { to: '/admin/settings', icon: UserCog, label: 'Admin Users' },
  { to: '/admin/audit', icon: History, label: 'Audit Log' },
];

const AdminLayout = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="flex h-screen bg-white" data-testid="admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-[240px] bg-[#111827] text-white
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-[#1A6B3C]">CarbiQ</span>
            <span className="text-xs bg-gray-600 text-gray-300 px-2 py-0.5 rounded">Admin</span>
          </div>
          <button 
            className="lg:hidden text-gray-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto sidebar-scroll">
          {navItems.map((item, index) => (
            item.divider ? (
              <div key={`divider-${index}`} className="px-4 py-2 mt-4 mb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{item.label}</span>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 text-sm
                  transition-colors duration-150
                  ${isActive 
                    ? 'text-[#1A6B3C] bg-gray-800 border-l-4 border-[#1A6B3C]' 
                    : 'text-gray-300 hover:text-white hover:bg-gray-800 border-l-4 border-transparent'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            )
          ))}
        </nav>

        {/* Sign Out */}
        <div className="p-4 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full text-sm text-gray-300 hover:text-white hover:bg-gray-800 rounded transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} />
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-[#1A6B3C]">CarbiQ</span>
            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Admin</span>
          </div>
          <div className="w-10" /> {/* Spacer for alignment */}
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
